import { DAYS } from "@p0security/util/time";
import { compact, mapValues, uniq } from "lodash-es";

import type {
  DslAliases,
  DslMapping,
  DslParser,
  NodeTerm,
} from "../graph/dsl/index.ts";
import { parse } from "../graph/dsl/index.ts";
import type { NodePredicate } from "../graph/search.ts";
import { isNode } from "../graph/types.ts";
import type { Provider } from "../types/assessment.ts";
import type { AssessmentNodes } from "../types/assessment/data/index.ts";
import { AssessmentSchema } from "../types/assessment/data/index.ts";

export const STALE_CREDENTIAL_MILLIS = 90 * DAYS;

/** Converts special node types to values */
export const AssessmentMap: DslMapping<AssessmentNodes> = {
  usage: {
    keys: (n) => [n.data.type],
    attributes: {
      last90: (n) => [n.data.type],
    },
  },
  identity: {
    attributes: {
      // accessFoo are short term hacks until we make standalone directory assessment scopes
      accessAdd: (n) => compact([n.data.access?.add]),
      accessApprove: (n) => compact([n.data.access?.approve]),
      accessJoin: (n) => compact([n.data.access?.join]),
      accessView: (n) => compact([n.data.access?.view]),
      // ---
      type: (n) => [
        n.data.type,
        ...(n.data.type === "service-account" ? ["role"] : []),
        ...(n.data.isProviderManaged ? ["service-agent"] : []),
      ],
      status: (n) => [n.data.disabled ? "disabled" : "active"],
      trustedAccountPrincipals: (n) => [
        ...(n.data.trustedAccountPrincipals ?? []),
      ],
    },
    keys: (n) => compact([n.key, n.data.label]),
  },
  risk: {
    keys: (n) => compact([n.key, n.data.score]),
  },
  grant: {
    keys: (n) => compact([n.key, n.data.principal, n.data.privilegeSet]),
    attributes: {
      resource: (n) => [...n.data.resources],
      role: (n) => (n.data.provider !== "aws" ? [n.data.privilegeSet] : []),
      status: (n) => [n.data.disabled ? "disabled" : "enabled"],
    },
  },
  credential: {
    attributes: {
      enabledKey: (n) => [
        n.data.type === "key" && n.data.status === "enabled" ? "true" : "false",
      ],
      stale90: (n) => [
        // Short lived credentials cannot be stale
        n.data.type !== "short-lived" &&
        n.data.createdTime &&
        n.data.createdTime < Date.now() - STALE_CREDENTIAL_MILLIS
          ? "true"
          : "false",
      ],
      last40: (n) => [
        (n.data.lastAuthnTime ?? 0) > Date.now() - 40 * DAYS
          ? "used"
          : "unused",
      ],
      last90: (n) => [
        (n.data.lastAuthnTime ?? 0) > Date.now() - 90 * DAYS
          ? "used"
          : "unused",
      ],
      last365: (n) => [
        // If analyzer auth time is not specified or the key was created less than 365 days ago, this usage is unknown
        n.data.maxAuthnLookbackDays && n.data.maxAuthnLookbackDays >= 365
          ? (n.data.lastAuthnTime ?? 0) > Date.now() - 365 * DAYS
            ? "used"
            : n.data.createdTime !== undefined &&
                n.data.createdTime < Date.now() - 365 * DAYS
              ? "unused"
              : "unknown"
          : "unknown",
      ],
      expiryIn30: (n) => [
        n.data.expirationTime &&
        n.data.expirationTime > Date.now() &&
        n.data.expirationTime < Date.now() + 30 * DAYS
          ? "true"
          : "false",
      ],
      expiryAfter365: (n) => [
        n.data.expirationTime && n.data.expirationTime > Date.now() + 365 * DAYS
          ? "true"
          : "false",
      ],
      // "true" for federated credentials whose subject (claim filter) is
      // missing, empty, or contains a wildcard — meaning any token from the
      // trusted issuer can authenticate, not just a specific workload.
      wildcardSubject: (n) => [
        n.data.type === "federated" &&
        (!n.data.subject ||
          n.data.subject.trim() === "" ||
          n.data.subject.includes("*"))
          ? "true"
          : "false",
      ],
    },
  },
};

const addAttributes =
  (type: keyof AssessmentNodes, attributes: Record<string, string>) =>
  (ast: NodeTerm) => {
    const { properties } = ast;
    return {
      ...ast,
      type,
      properties: [
        ...properties,
        ...Object.entries(attributes).map(([attribute, keyword]) => ({
          attribute,
          keyterm: { mode: "e" as const, invert: false, keyword },
        })),
      ],
    };
  };

const addProvider = (type: keyof AssessmentNodes, provider: Provider) =>
  addAttributes(type, { provider });

const removeProvider =
  (type: keyof AssessmentNodes, provider: Provider) => (ast: NodeTerm) => {
    const { properties } = ast;
    return {
      ...ast,
      type,
      properties: [
        ...properties,
        {
          attribute: "provider",
          keyterm: { mode: "e" as const, invert: true, keyword: provider },
        },
      ],
    };
  };

const AssessmentAliases: DslAliases = {
  entitlement: (ast) => ({ ...ast, type: "grant" }),
  awsIamRole: addAttributes("identity", {
    provider: "aws",
    type: "aws-iam-role",
  }),
  awsPolicy: addProvider("privilegeSet", "aws"),
  awsPolicyAssignment: addProvider("grant", "aws"),
  gcpRole: addProvider("privilegeSet", "gcp"),
  gcpRoleBinding: addProvider("grant", "gcp"),
  role: removeProvider("privilegeSet", "aws"),
};

export const identityPredicate: NodePredicate<AssessmentNodes> = (n) =>
  isNode("identity")(n) && n.data.type !== "unknown";

export const grantPredicate: NodePredicate<AssessmentNodes> =
  // 2024-09-17 "binding" alias temporary to shim node name changes.
  // Remove it after 2024-09-24.
  (n) => isNode("grant")(n) || isNode("binding")(n as any);

export const resourcePredicate: NodePredicate<AssessmentNodes> =
  isNode("resource");

const assessmentTypes: Record<string, string[]> = mapValues(
  AssessmentSchema,
  (v, k) =>
    uniq([
      ...Object.keys(v.attributes),
      ...Object.keys(
        AssessmentMap[k as keyof AssessmentNodes]?.attributes ?? {}
      ),
    ])
);

export const assessmentParse = (query: string) => (parser: DslParser) =>
  parse({
    mapping: AssessmentMap,
    aliases: AssessmentAliases,
    parser,
    types: assessmentTypes,
  })(query);
